/**
 * Union Analytics Dashboard — /associations/:id/analytics
 *
 * Private view for the union's owner (or platform admin). Access is
 * probed server-side via /api/associations/:id/analytics/access — a
 * 403 from the payload endpoint bounces the user out.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft, Users, TrendingUp, TrendingDown, ArrowRight, Search,
  ShieldCheck, Download, Mail, Phone,
} from "lucide-react";

const ROLE_LABELS = {
  lighting: "Lighting", grip: "Grip", sound: "Sound", makeup: "Make-up",
  spot_boy: "Spot Boy", camera: "Camera", dop: "DoP", director: "Director",
  ad: "AD", producer: "Producer", editor: "Editor", vfx: "VFX",
  costume: "Costume", stunt: "Stunt", choreographer: "Choreo",
  main_talent: "Main Talent", music: "Music",
  journalist: "Journalist", talent_model: "Model",
};

export default function AssociationAnalytics() {
  const { id }  = useParams();
  const nav     = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setL]     = useState(true);
  const [q, setQ]           = useState("");
  const [offset, setOffset] = useState(0);
  const LIMIT               = 50;

  async function load() {
    setL(true);
    try {
      const { data } = await api.get(
        `/associations/${id}/analytics`,
        { params: { weeks: 12, limit: LIMIT, offset } },
      );
      setData(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error("You need to be this union's admin to see analytics");
        nav(`/associations/${id}`);
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load analytics");
    } finally { setL(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id, offset]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-xs">
        Loading analytics…
      </div>
    );
  }
  if (!data) return null;

  const color = data.association.color;
  const trend = data.totals.this_week - data.totals.prev_week;
  const filtered = q.trim()
    ? data.roster.items.filter(r =>
        (r.name       || "").toLowerCase().includes(q.trim().toLowerCase()) ||
        (r.primary_role || "").toLowerCase().includes(q.trim().toLowerCase()) ||
        (r.email      || "").toLowerCase().includes(q.trim().toLowerCase()))
    : data.roster.items;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="association-analytics">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button onClick={() => nav(`/associations/${id}`)}
              className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
              data-testid="analytics-back">
              <ArrowLeft size={12} /> {data.association.name} profile
            </button>
            <div className="mt-2 text-[10px] tracking-[0.32em] uppercase" style={{ color }}>
              Union Analytics
            </div>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl uppercase tracking-tight"
              style={{ color }}
              data-testid="analytics-title">
              {data.association.name}
            </h1>
            <div className="mt-1 text-white/55 text-sm">{data.association.full}</div>
          </div>
          <ShieldCheck size={16} className="mt-2" style={{ color }} />
        </div>

        {/* KPI strip */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total members" value={data.totals.members}
               color={color} testid="kpi-total" Icon={Users} />
          <Kpi label="This week"    value={data.totals.this_week} color={color}
               testid="kpi-this-week" />
          <Kpi label="Prev week"    value={data.totals.prev_week} color="#9CA3AF"
               testid="kpi-prev-week" />
          <Kpi label="Week-over-week" value={`${trend >= 0 ? "+" : ""}${trend}`}
               color={trend >= 0 ? "#10B981" : "#EF4444"}
               Icon={trend >= 0 ? TrendingUp : TrendingDown}
               testid="kpi-wow" />
        </div>

        {/* Weekly chart */}
        <div className="mt-6 cs-card rounded-2xl p-6" data-testid="analytics-weekly-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                Weekly signups
              </div>
              <div className="text-sm text-white/85 mt-1">Last 12 weeks</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data.weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)"
                       tick={{ fontSize: 10 }} />
                <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 10 }}
                       allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#0f0f14",
                                  border: "1px solid rgba(255,255,255,0.15)",
                                  borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#fff" }}
                  cursor={{ fill: `${color}22` }} />
                <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Roles breakdown + Roster in a grid */}
        <div className="mt-6 grid lg:grid-cols-3 gap-4">

          {/* Role breakdown */}
          <div className="cs-card rounded-2xl p-6" data-testid="analytics-role-breakdown">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-4">
              Roles breakdown
            </div>
            {data.role_breakdown.length === 0 && (
              <div className="text-sm text-white/50">No members yet.</div>
            )}
            <ul className="space-y-2">
              {data.role_breakdown.map(r => {
                const pct = data.totals.members > 0
                  ? Math.round(100 * r.count / data.totals.members) : 0;
                return (
                  <li key={r.role} className="text-xs" data-testid={`role-row-${r.role}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/80">
                        {ROLE_LABELS[r.role] || r.role}
                      </span>
                      <span className="text-white/60 tabular-nums">
                        {r.count} <span className="text-white/35">· {pct}%</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full"
                        style={{ background: color, width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Roster table */}
          <div className="cs-card rounded-2xl p-6 lg:col-span-2" data-testid="analytics-roster">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                  Member roster
                </div>
                <div className="text-sm text-white/85 mt-1">
                  {data.roster.total} total ·
                  showing {data.roster.offset + 1}–{Math.min(data.roster.offset + data.roster.items.length, data.roster.total)}
                </div>
              </div>
              <div className="relative flex-1 max-w-[280px]">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Filter this page…"
                  className="w-full pl-8 pr-3 py-2 bg-black/40 border border-white/10 rounded-lg text-xs"
                  data-testid="roster-search" />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="roster-table">
                <thead>
                  <tr className="text-left text-white/40 uppercase tracking-[0.18em] text-[9px]">
                    <th className="pb-2">Name</th>
                    <th className="pb-2">Role</th>
                    <th className="pb-2">Membership #</th>
                    <th className="pb-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-white/40" data-testid="roster-empty">
                      No members match this filter.
                    </td></tr>
                  )}
                  {filtered.map(r => (
                    <tr key={r.id} className="border-t border-white/5"
                      data-testid={`roster-row-${r.id}`}>
                      <td className="py-2.5 pr-3">
                        <div className="text-white/90 font-medium">{r.name}</div>
                        {r.email && <div className="text-[10px] text-white/45 mt-0.5">{r.email}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-white/70">
                        {ROLE_LABELS[r.primary_role] || r.primary_role}
                      </td>
                      <td className="py-2.5 pr-3 text-white/60 font-mono">
                        {r.membership_number || <span className="text-white/25">—</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-white/55">
                        {r.joined_at ? new Date(r.joined_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.roster.total > LIMIT && (
              <div className="mt-4 flex items-center justify-between text-xs">
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  className="uppercase tracking-[0.2em] text-[10px] text-white/60 hover:text-white disabled:opacity-30 inline-flex items-center gap-1"
                  data-testid="roster-prev">
                  <ArrowLeft size={11} /> Prev
                </button>
                <div className="text-white/40 text-[10px]">
                  Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(data.roster.total / LIMIT)}
                </div>
                <button
                  disabled={offset + LIMIT >= data.roster.total}
                  onClick={() => setOffset(offset + LIMIT)}
                  className="uppercase tracking-[0.2em] text-[10px] text-white/60 hover:text-white disabled:opacity-30 inline-flex items-center gap-1"
                  data-testid="roster-next">
                  Next <ArrowRight size={11} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, Icon, testid }) {
  return (
    <div className="cs-card rounded-2xl p-5" data-testid={testid}
      style={{ borderColor: `${color}22` }}>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={12} style={{ color }} />}
        <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color }}>
          {label}
        </div>
      </div>
      <div className="mt-2 text-3xl font-display tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
